import React from "react";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { BottomNavigation, BottomNavigationAction } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

import { updateSelectedOverlay } from "../store/overlay/OverlayActions";
// import InventoryOverlayLabel from "./overlays/InventoryOverlayLabel";
// import AttributesOverlayLabel from "./overlays/AttributesOverlayLabel";
import CategoriesOverlayLabel from "./overlays/CategoriesOverlayLabel";
import AnalyticsOverlayLabel from "./overlays/AnalyticsOverlayLabel";
import ProductBadgeOverlayLabel from "./overlays/ProductBadgeOverlayLabel";
import { selectOverlay } from "../store/overlay/OverlaySelectors";
import { OVERLAY_TABS } from "../utils/Constants";
import RatingsOverlayLabel from "./overlays/RatingsOverlayLabel";
import { classicTheme } from "../styles/themes/classic";
import SmAiOverlayLabel from "./overlays/SmAiOverlayLabel";
import {
  selectCallerAccountId,
  selectAccountFeatures,
  selectConfigValue,
} from "store/app-config/AppConfigSelectors";
import AppState from "store/AppState";
import { AIOverlayIcon } from "assets";
import { selectIsAnalyticsEnabled } from "store/adapter-config/AdapterConfigSelectors";
import TooltipComponent from "./common/ToolTip";
import { useHasEntitlement } from "hooks/useEntitlement";
import HeatMapButton from "./HeatMapButton";
export { addAttributeModalId } from "./add-attribute-modal/AddAttributeModal";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: " grid",
      justifyItems: "flex-start",
      height: "fit-content",
      "& .Mui-selected": {
        "&:hover": {
          color: classicTheme.palette.primary.main,
        },
      },
      "& .MuiBottomNavigationAction-root": {
        flex: "inherit",
        minWidth: 0,
        overflow: "hidden",
        fontWeight: "bold",
        "&:hover": {
          color: classicTheme.palette.primaryHoverColor?.main,
        },
        margin: 5,
      },
      "& .MuiBottomNavigationAction-wrapper": {
        display: "block",
      },
      "& .MuiBottomNavigationAction-label": {
        fontSize: "0.875rem",
        marginLeft: "8px",
      },
      "& .MuiBottomNavigationAction-label.Mui-selected": {
        fontSize: "14px",
        color: classicTheme.palette.primary.main,
        fontWeight: "bold",
      },
      "& .MuiBottomNavigationAction-root.Mui-selected": {
        color: classicTheme.palette.primary.main,
      },
      "& .MuiSvgIcon-root": {
        color: classicTheme.palette.primary.main,
      },
    },
    bottomNavigationAction: {
      transition: "padding 500ms ease-out",
    },
    bottomNavigationActionActive: {
      paddingBottom: theme.spacing(5),
      transition: "padding 500ms ease-in",
    },
    disabled: {
      opacity: 0.4,
    },
    analyticsLabel: {
      pointerEvents: "none",
      "&.MuiBottomNavigationAction-root.MuiBottomNavigationAction-iconOnly": {
        paddingTop: 6,
      },
      "& .MuiBottomNavigationAction-label": {
        opacity: 1,
      },
    },
  }),
);

interface Props {}

const OverlayTabs: React.FC<Props> = () => {
  const intl = useIntl();
  const selectedOverlay = useSelector(selectOverlay);
  const classes = useStyles();
  const dispatch = useDispatch();
  const isBoostBuryEnabled = useSelector((state: AppState) =>
    selectConfigValue(state, "enableBoostBury"),
  );
  const isSubstituionEnabled = useSelector((state: AppState) =>
    selectConfigValue(state, "enableSubstitution"),
  );
  const isProductBadgeEnabled =
    useSelector((state: AppState) =>
      selectConfigValue(state, "badgeOverlayEnabled"),
    ) === "true";
  const isAiFeatureEnabled =
    isBoostBuryEnabled === "true" || isSubstituionEnabled === "true";
  const accountId = useSelector(selectCallerAccountId);
  const features = useSelector((state: AppState) =>
    selectAccountFeatures(state, accountId),
  );
  const isRatingsFeatureEnabled = features?.["ratings-overlay"] ?? false;
  const isAnalyticsEnabled = useSelector((state: AppState) =>
    selectIsAnalyticsEnabled(state),
  );

  const hasAnalyticsEntitlement = useHasEntitlement("analytics");
  const hasAiEntitlement = useHasEntitlement("ai");

  /**
   * Heat Map button is rendered nested under the Analytics tab ONLY when
   * the Analytics overlay is currently selected — there is a dependency on
   * the Analytics data loading, so we only expose Heat Map after the user
   * has activated the Analytics overlay.
   */
  const isAnalyticsOverlaySelected =
    selectedOverlay === OVERLAY_TABS.ANALYTICS_OVERLAY;

  return (
    <div>
      <BottomNavigation
        value={selectedOverlay}
        className={classes.root}
        onChange={(event, newValue) => {
          if (selectedOverlay !== newValue) {
            dispatch(updateSelectedOverlay(newValue));
          }
        }}
        showLabels
      >
        <BottomNavigationAction
          label={intl.formatMessage({
            id: "overlayTabs.productsLabel",
            defaultMessage: "Products",
          })}
          disableRipple
          icon={<i className="fas fa-boxes" />}
          value={OVERLAY_TABS.PRODUCTS}
        />
      </BottomNavigation>

      {/*
        Analytics tab + nested Heat Map button live in the same wrapper
        so they are visually and structurally grouped as one unit.
      */}
      {hasAnalyticsEntitlement && (
        <div>
          <BottomNavigation
            value={selectedOverlay}
            className={classes.root}
            onChange={(event, newValue) => {
              if (selectedOverlay !== newValue) {
                if (
                  newValue === OVERLAY_TABS.ANALYTICS_OVERLAY &&
                  isAnalyticsEnabled === false
                ) {
                  return;
                }
                dispatch(updateSelectedOverlay(newValue));
              }
            }}
            showLabels
          >
            {isAnalyticsEnabled === false ? (
              <TooltipComponent
                tooltipTitle={intl.formatMessage({
                  id: "overlayTabs.analyticsDisabledTooltip",
                  defaultMessage:
                    "The Analytics overlay is not available for the selected store",
                })}
              >
                <BottomNavigationAction
                  label={<AnalyticsOverlayLabel />}
                  disableRipple
                  icon={<i className="fas fa-chart-line" />}
                  value={OVERLAY_TABS.ANALYTICS_OVERLAY}
                  disabled={isAnalyticsEnabled === false}
                  className={
                    isAnalyticsEnabled === false
                      ? `${classes.analyticsLabel} ${classes.disabled}`
                      : ""
                  }
                />
              </TooltipComponent>
            ) : (
              <BottomNavigationAction
                label={<AnalyticsOverlayLabel />}
                disableRipple
                icon={<i className="fas fa-chart-line" />}
                value={OVERLAY_TABS.ANALYTICS_OVERLAY}
              />
            )}
          </BottomNavigation>
          {isAnalyticsOverlaySelected && <HeatMapButton />}
        </div>
      )}

      <BottomNavigation
        value={selectedOverlay}
        className={classes.root}
        onChange={(event, newValue) => {
          if (selectedOverlay !== newValue) {
            dispatch(updateSelectedOverlay(newValue));
          }
        }}
        showLabels
      >
        {isRatingsFeatureEnabled && (
          <BottomNavigationAction
            label={<RatingsOverlayLabel />}
            disableRipple
            icon={<i className="fas fa-star" />}
            value={OVERLAY_TABS.RATINGS}
          />
        )}
        <BottomNavigationAction
          label={<CategoriesOverlayLabel />}
          disableRipple
          icon={<i className="fas fa-sitemap" />}
          value={OVERLAY_TABS.CATEGORIES_OVERLAY}
        />
        {isProductBadgeEnabled && (
          <BottomNavigationAction
            label={<ProductBadgeOverlayLabel />}
            disableRipple
            icon={<i className="fas fa-tags" />}
            value={OVERLAY_TABS.PRODUCTS_BADGES}
          />
        )}
        {hasAiEntitlement && hasAnalyticsEntitlement && isAiFeatureEnabled && (
          <BottomNavigationAction
            label={<SmAiOverlayLabel />}
            disableRipple
            icon={<AIOverlayIcon />}
            value={OVERLAY_TABS.SMAI}
          />
        )}
        {/* <BottomNavigationAction
          label={<InventoryOverlayLabel />}
          disableRipple
          icon={<i className="fas fa-warehouse" />}
          className={
            selectedOverlay === OVERLAY_TABS.INVENTORY_OVERLAY
              ? classes.bottomNavigationActionActive
              : classes.bottomNavigationAction
          }
          value={OVERLAY_TABS.INVENTORY_OVERLAY}
        />
        <BottomNavigationAction
          label={<AttributesOverlayLabel />}
          disableRipple
          icon={<i className="fas fa-tags" />}
          className={
            selectedOverlay === OVERLAY_TABS.ATTRIBUTES_OVERLAY
              ? classes.bottomNavigationActionActive
              : classes.bottomNavigationAction
          }
          value={OVERLAY_TABS.ATTRIBUTES_OVERLAY}
        /> */}
      </BottomNavigation>
    </div>
  );
};

export default OverlayTabs;
